<!-- Line selection -->
@if (invoiceLines && !creditLines) {
  <panel width="400">
    <h1>{{ 'credit-wizard.step1.select-lines-header' | translate }}</h1>
    <list-view [selectable]="false">
      <row [disabled]="line.suspendedAccountingPlan" *listData="let line from viableInvoiceLines" [highlighted]="selectedLines.indexOf(line) >= 0" (click)="toggleSelectedLine(line)" [statusColor]="line.suspendedAccountingPlan ? 'color-red-50' : ''" [statusTitle]="line.suspendedAccountingPlan ? 'credit-wizard.step1.line-is-apart-of-settlement' : undefined">
        <column>{{ line.description }}</column>
        <column align-right>{{ line.amount == 0 ? '' : (line.amount | currency) }}</column>
      </row>
    </list-view>
    <section>
      <button [loader]="false" [active]="selectedLines.length > 0" [ignoreValidation]="true" (click)="lockSelectedLines()">
        {{ (selectedLines.length === 0 ? 'credit-wizard.step1.select-lines' : 'credit-wizard.step1.credit-selected-lines') | translate }}
      </button>
    </section>
  </panel>
}
<!-- // Line selection -->

<!-- Selected lines overview -->
@if (invoiceLines && creditLines) {
  <panel width="400">
    <h1>{{ 'credit-wizard.step2.selected-lines-header' | translate }}</h1>
    <list-view [selectable]="false">
      <row [ngClass]="{'out-of-focus': (creditLines.indexOf(line) < 0) }" *listData="let line from viableInvoiceLines" [highlighted]="currentLine === line" [clickable]="creditLines.indexOf(line) >= 0" [statusColor]="lineStatusColor(line)" (action)="currentLine = line">
        <column>{{ line.description }}</column>
        <column align-right>{{ line.amount == 0 ? '' : (line.amount | currency) }}</column>
      </row>
    </list-view>
    <section>
      <button [active]="readyToComplete" [ignoreValidation]="true" (click)="completeWizard()">
        {{ 'credit-wizard.step2.complete' | translate }}
      </button>
    </section>
  </panel>
}
<!-- // Selected lines overview -->

@if (loading && creditLines && !currentLine) {
  <panel width="400">
    <h1><skeleton /></h1>
    <skeletons [defaultSkeletons]="creditLines.length" />
  </panel>
}

<!-- Line steps -->
@if (currentLine && currentLineRequest && currentStepState) {
  <panel width="400">
    @if (!currentStepState.lineIsFee) {
      <h1>{{ currentLine.description }}</h1>
    }
    @if (currentStepState.lineIsFee) {
      <h1>{{ 'credit-wizard.step2.credit-fee-header' | translate }}</h1>
    }
    <list-view [selectable]="false" emptyLabel="">
      <row *listData="let step from currentStepState.subSteps" [hidden]="!step.completed && step.type != currentSubStep" [highlighted]="step.type == currentSubStep" [clickable]="false" [statusColor]="step.completed ? 'color-green-50' : 'color-yellow-50'">
        <column>{{ 'credit-wizard.step2.sub-step.'+steps[step.type] | translate }}</column>
        <column>{{ stepValue(step.type) }}</column>
      </row>
    </list-view>
    <!-- Step details -->
    @if (currentSubStep === steps.exemptFromFee) {
      <section>
        @if (!this.currentlyExemptedFromFees) {
          <custom-input [label]="'credit-wizard.step2.inputs.'+steps[currentSubStep] | translate" [inputType]="inputType.Checkbox" [(value)]="request.noHandlingFees" />
        }
        @if (this.currentlyExemptedFromFees) {
          {{ 'credit-wizard.step2.already-exempt-from-handling-fees' | translate }}
        }
      </section>
    }
    @if (currentSubStep === steps.sendNewInvoice) {
      <section>
        <div>{{ 'credit-wizard.step2.inputs.'+steps[currentSubStep] | translate }}</div>
      </section>
    }
    @if (currentSubStep === steps.newInvoiceAmount) {
      <section>
        {{ 'credit-wizard.step2.inputs.'+steps[currentSubStep] | translate }}
        <custom-input [placeholder]="'credit-wizard.step2.inputs.'+steps[currentSubStep]+'.value' | translate" [inputType]="inputType.Currency" [required]="true" [(value)]="currentStepState!.newInvoiceAmount" />
      </section>
    }
    @if (currentSubStep === steps.changeRemainingInvoicePosts) {
      <section>
        <div>{{ 'credit-wizard.step2.inputs.'+steps[currentSubStep] | translate }}</div>
      </section>
    }
    @if (currentSubStep === steps.editLedgerPosts) {
      <section one-column>
        <div>{{ 'credit-wizard.step2.inputs.'+steps[currentSubStep] | translate }}</div>
        <div>{{ 'credit-wizard.step2.inputs.'+steps[currentSubStep]+'.expected-new-value' | translate }}: <strong>{{ currentStepState.expectedTotalValue }}</strong></div>
      </section>
    }
    @if (currentSubStep === steps.creditAmount) {
      <section>
        {{ 'credit-wizard.step2.inputs.'+steps[currentSubStep] | translate }}
        <custom-input [placeholder]="'credit-wizard.step2.inputs.'+steps[currentSubStep]+'.amount' | translate" [inputType]="inputType.Currency" [required]="true" [(value)]="currentLineRequest.amount" [customValidState]="currentLineRequest.amount > 0 && currentLineRequest.amount <= maxValueToCredit(currentStepState)" [customValidationMessage]="'credit-wizard.validation.amount' | translate: { 'maxValue':  maxValueToCredit(currentStepState) | currency }"></custom-input>
      </section>
    }
    @if (currentSubStep === steps.updateAnnualValueForRenewal) {
      <section>
        <div>{{ 'credit-wizard.step2.inputs.'+steps[currentSubStep] | translate: {'ACV' : (currentStepState!.annualContractValue | currency), 'AnnualValueForRenewal': (currentStepState!.accountingPlan!.productPlan!.annualValueForRenewal | currency)} }}</div>
      </section>
    }
    @if (currentSubStep === steps.endActiveAccountingPlan) {
      <section>
        <div>{{ 'credit-wizard.step2.inputs.'+steps[currentSubStep] | translate }}</div>
      </section>
    }
    @if (currentSubStep === steps.newEndDate) {
      <section>
        <date-input [label]="'credit-wizard.step2.inputs.'+steps[currentSubStep]+'.end-date' | translate" [(value)]="currentStepState.planEndDate" (valueChange)="updateLedgerPosts($event)" [required]="currentStepState.endActiveAccountingPlan" [disabled]="!currentStepState.endActiveAccountingPlan" [minDate]="currentStepState.endDatesFilter?.[0] ?? minEndDate" [filterDates]="currentStepState.endDatesFilter" />
        @if (!currentStepState.existingTerminationDateForProductPlan) {
          <date-input [label]="'credit-wizard.step2.inputs.'+steps[currentSubStep]+'.termination-date' | translate" [(value)]="currentStepState.planTerminationDate" [required]="currentStepState.endActiveAccountingPlan" [disabled]="!currentStepState.endActiveAccountingPlan" />
        }
        @else {
          <date-input [label]="'credit-wizard.step2.inputs.'+steps[currentSubStep]+'.termination-date' | translate" [value]="currentStepState.existingTerminationDateForProductPlan" [disabled]="true" />
        }
      </section>
    }
    @if (currentSubStep === steps.terminationReason) {
      <section>
        <reason-select [type]="reasonType.Termination" (valueChange)="currentStepState.terminationReason = $event" />
      </section>
    }
    <!-- // Step details -->
    <section>
      @if (!currentStepIsTrueFalse) {
        <button [loader]="false" [active]="currentSubStep != undefined" [allowWhenPristine]="true" (click)="completeCurrentSubStep()">{{ 'credit-wizard.step2.save' | translate }}</button>
      }
      @if (currentStepIsTrueFalse) {
        <button [loader]="false" confirm [allowWhenPristine]="true" (click)="completeCurrentSubStep(true)">{{ 'general.true' | translate }}</button>
      }
      @if (currentStepIsTrueFalse) {
        <button [loader]="false" reject [allowWhenPristine]="true" (click)="completeCurrentSubStep(false)">{{ 'general.false' | translate }}</button>
      }
      <button [loader]="false" ghost [active]="currentStepState != undefined" [ignoreValidation]="true" (click)="resetCurrentStep()">{{ 'credit-wizard.step2.reset' | translate }}</button>
    </section>
  </panel>
}
<!-- // Line steps -->

@if (currentStepState && currentStepState.accountingPlan) {
  <panel width="600">
    <h1>{{ 'ledger-posts.header' | translate }}: {{ currentStepState.accountingPlan.productPlan | productName }}</h1>
    <ledger-posts #ledgerPosts (totalLedgerValueChanged)="updateExpectedTotalValueForCurrentStep($event)" [overridePosts]="currentStepState.allLedgerPosts" [productPlans]="[currentStepState.accountingPlan.productPlan]" [displayProductName]="false" [allowChangingTotalValue]="true" [editMode]="currentSubStep == steps.editLedgerPosts" [accountingPlan]="currentStepState.accountingPlan" />
  </panel>
}
